// components/TreatmentTimer.tsx
import React, { CSSProperties, useEffect, useState } from 'react';
import { confirmButton } from '../../../components/Buttons';
import './TreatmentTimer.scss'

interface TreatmentTimerProps {
  onDoseGiven?: (givenAt: Date) => void;
}

const DEFAULT_MINUTES = 20;
const TIMER_STORAGE_KEY = 'ASTHMA_CURRENT_TIMER_TARGET';

const TreatmentTimer: React.FC<TreatmentTimerProps> = ({ onDoseGiven }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [timerTarget, setTimerTarget] = useState<number | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [lastReportedTarget, setLastReportedTarget] = useState<number | null>(null);
  const [alertSent, setAlertSent] = useState(false);

  // edit time
  const [editDurationSec, setEditDurationSec] = useState<number>(DEFAULT_MINUTES * 60);
  const [editHours, setEditHours] = useState<number>(0);
  const [editMinutes, setEditMinutes] = useState<number>(DEFAULT_MINUTES);

  // 1. load from memory
  useEffect(() => {
    const savedTarget = localStorage.getItem(TIMER_STORAGE_KEY);
    if (savedTarget) {
      const targetTimestamp = parseInt(savedTarget, 10);
      if (!isNaN(targetTimestamp)) {
        setTimerTarget(targetTimestamp);
      }
    }
  }, []);

  // 2. update time every second
  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  // === time calculation logic (including negative) ===   
  let remainingMs: number | null = null;
  if (timerTarget !== null) {
    remainingMs = timerTarget - now;
  }

  // calculate seconds (works also for negative numbers)
  let remainingSec = 0;
  if (remainingMs !== null) {
    // if positive: round down, if negative: round up (to prevent strange jumps at 0)
    remainingSec = remainingMs > 0
      ? Math.floor(remainingMs / 1000)
      : Math.ceil(remainingMs / 1000);
  }

  const isOverdue = remainingSec < 0; // did we pass the time?

  const [notifPermission, setNotifPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );

  // Request permission on mount (desktop) 
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(p => setNotifPermission(p));
    }
  }, []);

  // Play a short double-beep using Web Audio API
  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const beep = (startTime: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.4, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
        osc.start(startTime);
        osc.stop(startTime + 0.3);
      };
      beep(ctx.currentTime);
      beep(ctx.currentTime + 0.4);
    } catch { /* ignore audio errors */ }
  };

  // alert to doctor when the time passes to minus
  useEffect(() => {
    if (timerTarget !== null && isOverdue && !alertSent) {
      setAlertSent(true); // set first to prevent double-fire in StrictMode
      try {
        playBeep();
        // Check Notification.permission directly (not stale state)
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('הזמן עבר!', {
            body: 'נשלחה התראה לרופא',
          });
        } else {
          alert('הזמן עבר! נשלחה התראה לרופא');
        }
      } catch (err) {
        console.error('Notification error:', err);
        try { alert('הזמן עבר! נשלחה התראה לרופא'); } catch { /* ignore */ }
      }
    }
  }, [timerTarget, isOverdue, alertSent]);

  // === format function supporting minus ===
  const formatHMS = (totalSec: number): string => {
    const isNegative = totalSec < 0;
    const absSec = Math.abs(totalSec); // work with absolute value

    const hours = Math.floor(absSec / 3600);
    const minutes = Math.floor((absSec % 3600) / 60);
    const seconds = absSec % 60;

    const pad = (n: number) => n.toString().padStart(2, '0');
    // adding the minus at the beginning if needed
    return `${isNegative ? '-' : ''}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const summaryTimeDisplay = (() => {
    // if there is an active timer (even if the time has passed and it is negative)
    if (timerTarget !== null) {
      return formatHMS(remainingSec);
    }
    // default value (edit mode/start)
    return formatHMS(editDurationSec);
  })();

  // sync edit mode
  useEffect(() => {
    if (!isEditing) return;

    let baseSec: number;
    // if there is an active timer (even if it's negative), editing will start from it
    if (timerTarget !== null) {
      baseSec = Math.abs(remainingSec); // editing is always positive
    } else {
      baseSec = editDurationSec;
    }

    const h = Math.floor(baseSec / 3600);
    const m = Math.floor((baseSec % 3600) / 60);

    setEditHours(h);
    setEditMinutes(m);
  }, [isEditing, timerTarget, remainingSec, editDurationSec]);

  // report end + reset
  useEffect(() => {
    if (!onDoseGiven) return;
    if (timerTarget === null) return;

    // here we do not stop the timer automatically when it reaches 0
    // we wait for the user to press the button "start again/mark dose"

  }, [now, timerTarget, onDoseGiven, lastReportedTarget]);

  const handleHoursChange = (value: string) => {
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) return;
    setEditHours(num);
    setEditDurationSec(num * 3600 + editMinutes * 60);
  };

  const handleMinutesChange = (value: string) => {
    const num = Number(value);
    if (Number.isNaN(num) || num < 0 || num > 59) return;
    setEditMinutes(num);
    setEditDurationSec(editHours * 3600 + num * 60);
  };

  const handleQuickTime = (seconds: number) => {
    setEditDurationSec(seconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    setEditHours(h);
    setEditMinutes(m);
  };

  const handleUpdateTimerSettings = () => {
    setIsEditing(false);
  };

  const handleStartTimer = () => {
    // click on the main button

    // 1. if the timer was active (even if positive or negative) - it means the dose was given now
    if (timerTarget !== null) {
      if (onDoseGiven) {
        onDoseGiven(new Date());
      }
      // remove from memory for the next dose
      localStorage.removeItem(TIMER_STORAGE_KEY);
      setTimerTarget(null); // reset timer
      setAlertSent(false);
      return;
    }

    // 2. otherwise - start a new timer
    if (editDurationSec <= 0) return;

    // Request permission on user interaction (important for mobile)
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(p => setNotifPermission(p));
    }

    const nowMs = Date.now();
    const newTarget = nowMs + editDurationSec * 1000;
    setTimerTarget(newTarget);
    setAlertSent(false);
    localStorage.setItem(TIMER_STORAGE_KEY, newTarget.toString());
  };

  return (
    <div className="treatment-timer-wrapper">
      <div className="treatment-timer-label">יש לבצע הערכה חוזרת בעוד:</div>
      {!isEditing && (
        <div className="treatment-timer-summary-card">
          <div className="treatment-timer-summary-header">
            <span
              className={`treatment-timer-summary-time ${isOverdue ? 'timer-overdue' : ''}`}
              style={{
                direction: 'ltr'
              }}
            >
              {summaryTimeDisplay}
            </span>
            <button
              type="button"
              className="treatment-timer-summary-edit"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                style={{ width: '16px', height: '16px' }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
              </svg>

            </button>


          </div>

          <button
            type="button"
            style={{
              ...(confirmButton as CSSProperties),
              width: '100%',
              justifyContent: 'center',
              // change button color if it's overdue to draw attention
            }}
            onClick={handleStartTimer}
          >
            {/* the text changes according to the status */}
            {timerTarget !== null
              ? 'סמן מתן מנה (אפס טיימר)'
              : 'ניתנה תרופה - התחל טיימר'}
          </button>
        </div>
      )}

      {/* edit timer mode */}
      {isEditing && (
        <div className="treatment-timer-card">
          <div className="timer-question">
            בעוד כמה זמן נזכיר לך לגבי השלב הבא?
          </div>

          <div className="timer-digits-row">
            <div className="timer-column">
              <div className="timer-label">שעות</div>
              <input
                type="number"
                className="timer-main-value"
                value={editHours}
                min={0}
                onChange={(e) => handleHoursChange(e.target.value)}
              />
              <div className="timer-sub-value">
                {(editHours + 1).toString().padStart(2, '0')}
              </div>
            </div>

            <div className="timer-separator">:</div>

            <div className="timer-column">
              <div className="timer-label">דקות</div>
              <input
                type="number"
                className="timer-main-value"
                value={editMinutes}
                min={0}
                max={59}
                onChange={(e) => handleMinutesChange(e.target.value)}
              />
              <div className="timer-sub-value">
                {((editMinutes + 1) % 60).toString().padStart(2, '0')}
              </div>
            </div>
          </div>

          <div className="treatment-quick-times">
            <button
              type="button"
              className={`treatment-quick-time-btn ${editHours === 0 && editMinutes === 30 ? 'active' : ''}`}
              onClick={() => handleQuickTime(30 * 60)}
            >
              00:30:00
            </button>
            <button
              type="button"
              className={`treatment-quick-time-btn ${editHours === 0 && editMinutes === 20 ? 'active' : ''}`}
              onClick={() => handleQuickTime(20 * 60)}
            >
              00:20:00
            </button>
            <button
              type="button"
              className={`treatment-quick-time-btn ${editHours === 0 && editMinutes === 15 ? 'active' : ''}`}
              onClick={() => handleQuickTime(15 * 60)}
            >
              00:15:00
            </button>
          </div>

          <button
            type="button"
            className="timer-update-button"
            style={{
              ...(confirmButton as CSSProperties),
              width: '100%',
              justifyContent: 'center',
            }}
            onClick={handleUpdateTimerSettings}
          >
            עדכן
          </button>
        </div>
      )}
    </div>
  );
};

export default TreatmentTimer;